import { useState, useEffect, useRef, MutableRefObject } from 'react';
import { Loader2, TestTube, ChevronDown, MessageCircle, Calendar, ArrowDownCircle, UserCog, ScrollText, X, Volume2, AlertCircle } from 'lucide-react';
import { WhatsAppBubblePreview } from './WhatsAppBubblePreview';
import { FloatingSaveBar } from './FloatingSaveBar';
import TestAgentModal from '../CallAgent/TestAgentModal';
import { AgentRulesSection } from './AgentRulesSection';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import MultiSelectDropdown from '../shared/MultiSelectDropdown';
import {
  getAgentSettings,
  saveAgentIdentityConfig,
  generateAgentScript,
  getUserProfile,
  getWaTemplateSubmissions,
  submitWaTemplateForApproval,
  getWaTemplateApprovalStatus,
  getWaCampaignId,
  getAgentSettingsRow,
  upsertAgentSettingsRow,
  triggerReplyPromptGeneration,
  getConnectedWhatsAppAccounts,
} from '../../services/settingsService';
import type { ConnectedWhatsAppAccount } from '../../services/settingsService';
import { AgentAvatarUpload } from './AgentAvatarUpload';
import type {
  WhatsAppAgentSettings as WhatsAppAgentSettingsType,
  WhatsAppTemplateKey,
  WaTemplateSubmission,
  WaTemplateApprovalStatus,
} from '../../types/settings';
import { WA_TEMPLATE_DEFINITIONS } from '../../types/settings';
import { useAuth } from '../../contexts/AuthContext';
import { canEditAgentSettings } from '../../utils/permissions';
import { supabase } from '../../lib/supabase';
import { getWhatsAppDrafts, saveWhatsAppTemplatesAsDrafts } from '../../services/templateService';
import { DraftBanner } from './DraftBanner';
import { COMMON_NATIONALITIES, COUNTRY_CAPITAL_MAP, COMMON_LOCATIONS, TONE_OPTIONS } from '../../shared/callAgentConstants';
import MessagingStyleTooltip from '../shared/MessagingStyleTooltip';

const COLD_OUTREACH_TEMPLATES = WA_TEMPLATE_DEFINITIONS.filter(t => t.category === 'cold_outreach');
const INBOUND_TEMPLATES = WA_TEMPLATE_DEFINITIONS.filter(t => t.category === 'inbound');
const OTHER_TEMPLATES = WA_TEMPLATE_DEFINITIONS.filter(t => t.category === 'other');

function CategorySection({
  title,
  icon,
  templates,
  settings,
  onUpdateTemplate,
  onUpdateButton,
  onGenerateAI,
  configuredCount,
  totalCount,
  defaultOpen = true,
  submissions,
  approvedKeys,
  onSubmitForApproval,
  submittingKey,
  readOnly = false,
}: {
  title: string;
  icon: React.ReactNode;
  templates: typeof WA_TEMPLATE_DEFINITIONS;
  settings: WhatsAppAgentSettingsType;
  onUpdateTemplate: (key: WhatsAppTemplateKey, text: string) => void;
  onUpdateButton: (key: WhatsAppTemplateKey, buttonText: string, buttonUrl: string) => void;
  onGenerateAI: (section: string, tone: string, length: string, currentContent?: string) => Promise<string>;
  configuredCount: number;
  totalCount: number;
  defaultOpen?: boolean;
  submissions: Record<string, WaTemplateSubmission>;
  approvedKeys: string[];
  onSubmitForApproval: (key: WhatsAppTemplateKey, text: string, buttonText: string, buttonUrl: string) => Promise<void>;
  submittingKey: string | null;
  readOnly?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-gray-50 to-white hover:from-gray-100/80 hover:to-gray-50/50 transition-all duration-200"
      >
        <div className="flex items-center gap-3">
          {icon}
          <h4 className="text-sm font-bold text-gray-900 tracking-widest uppercase font-sans">{title}</h4>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 border border-[#1875FF]/15">
            <span className="text-xs font-bold text-[#1875FF]">{configuredCount}</span>
            <span className="text-xs text-gray-400">/</span>
            <span className="text-xs font-medium text-gray-500">{totalCount}</span>
          </div>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="px-6 pb-6 pt-2 space-y-3">
          {templates.map((tmpl) => {
            const templateData = settings.templates?.[tmpl.key];
            const submission = submissions[tmpl.key] || null;
            const hasApproved = approvedKeys.includes(tmpl.key);
            const status: WaTemplateApprovalStatus = getWaTemplateApprovalStatus(hasApproved, submission);

            return (
              <WhatsAppBubblePreview
                key={tmpl.key}
                title={`WA #${tmpl.number}: ${tmpl.label}`}
                content={templateData?.text || ''}
                placeholder={tmpl.placeholder}
                onUpdate={(text) => onUpdateTemplate(tmpl.key, text)}
                onGenerateAI={(tone, length, currentContent) => onGenerateAI(tmpl.key, tone, length, currentContent)}
                maxLength={1000}
                buttonText={templateData?.button_text || ''}
                buttonUrl={templateData?.button_url || ''}
                onUpdateButton={(btnText, btnUrl) => onUpdateButton(tmpl.key, btnText, btnUrl)}
                approvalStatus={status}
                pendingSubmission={submission}
                hasApprovedVersion={hasApproved}
                onSubmitForApproval={!readOnly ? (text, btnText, btnUrl) =>
                  onSubmitForApproval(tmpl.key, text, btnText, btnUrl)
                : undefined}
                submitting={submittingKey === tmpl.key}
                readOnly={readOnly}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

interface WhatsAppAgentSettingsProps {
  onDirtyStateChange?: (dirty: boolean) => void;
  saveRef?: MutableRefObject<(() => Promise<void>) | null>;
  discardRef?: MutableRefObject<(() => void) | null>;
}

export function WhatsAppAgentSettings({ onDirtyStateChange, saveRef, discardRef }: WhatsAppAgentSettingsProps) {
  const { user } = useAuth();
  const canEdit = canEditAgentSettings(user);
  const [settings, setSettings] = useState<WhatsAppAgentSettingsType | null>(null);
  const [originalSettings, setOriginalSettings] = useState<WhatsAppAgentSettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | undefined>();
  const [showTestModal, setShowTestModal] = useState(false);
  const [hasConfigChanges, setHasConfigChanges] = useState(false);
  const [whatsappAccounts, setWhatsappAccounts] = useState<ConnectedWhatsAppAccount[]>([]);
  const [whatsappAccountsLoading, setWhatsappAccountsLoading] = useState(false);
  const [isPlayingVoiceNote, setIsPlayingVoiceNote] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [submissions, setSubmissions] = useState<Record<string, WaTemplateSubmission>>({});
  const [approvedKeys, setApprovedKeys] = useState<string[]>([]);
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);
  const [waDrafts, setWaDrafts] = useState<Array<{ template_key: string; submitted_text: string; submitted_button_text: string; submitted_button_url: string; source: string }>>([]);
  const [waDraftsLoading, setWaDraftsLoading] = useState(false);
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | null>(null);
  const [nationalityInput, setNationalityInput] = useState('');
  const [showNationalitySuggestions, setShowNationalitySuggestions] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationInitialized, setLocationInitialized] = useState(false);
  const [campaignId, setCampaignId] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      await loadSettings();
      loadWaDrafts();
    };
    init();
    loadWhatsAppAccounts();
    getAgentSettingsRow('whatsapp').then(row => {
      if (row?.avatar_url) setAgentAvatarUrl(row.avatar_url);
    });
  }, []);

  const voiceNoteHydratedRef = useRef(false);
  useEffect(() => {
    if (voiceNoteHydratedRef.current) return;
    if (!settings?.whatsapp_account_id || whatsappAccounts.length === 0) return;
    const account = whatsappAccounts.find(a => a.id === settings.whatsapp_account_id);
    if (!account) return;
    voiceNoteHydratedRef.current = true;
    const needsUpdate =
      (!settings.voice_note_example && account.voice_note_example) ||
      (settings.voice_notes_allowed !== account.voice_notes_allowed) ||
      (settings.video_messages_allowed !== account.video_messages_allowed);
    if (needsUpdate) {
      const updated = {
        ...settings,
        voice_notes_allowed: account.voice_notes_allowed ?? settings.voice_notes_allowed,
        voice_note_example: account.voice_note_example || settings.voice_note_example || '',
        video_messages_allowed: account.video_messages_allowed ?? settings.video_messages_allowed,
      };
      setSettings(updated);
      setOriginalSettings(JSON.parse(JSON.stringify(updated)));
    }
  }, [settings?.whatsapp_account_id, whatsappAccounts]);

  useEffect(() => {
    if (!user?.client_id || !user?.email) return;

    const channel = supabase
      .channel('wa-template-approvals')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'whatsapp_templates',
          filter: `client_id=eq.${user.client_id}`,
        },
        (payload) => {
          const row = payload.new as any;

          if (row.status === 'approved') {
            setApprovedKeys(prev =>
              prev.includes(row.template_key) ? prev : [...prev, row.template_key]
            );
            setSubmissions(prev => {
              const next = { ...prev };
              delete next[row.template_key];
              return next;
            });
          } else if (row.status === 'rejected') {
            setSubmissions(prev => ({
              ...prev,
              [row.template_key]: {
                id: row.id,
                campaign_id: row.campaign_id,
                template_key: row.template_key,
                submitted_text: row.submitted_text,
                submitted_button_text: row.submitted_button_text,
                submitted_button_url: row.submitted_button_url,
                status: row.status,
                rejection_reason: row.rejection_reason,
                submitted_at: row.submitted_at,
                reviewed_at: row.reviewed_at,
              },
            }));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.client_id, user?.email]);

  useEffect(() => {
    if (settings && originalSettings) {
      const configChanged =
        settings.agent_name !== originalSettings.agent_name ||
        settings.gender !== originalSettings.gender ||
        JSON.stringify(settings.nationality) !== JSON.stringify(originalSettings.nationality) ||
        settings.location !== originalSettings.location ||
        settings.tone !== originalSettings.tone ||
        settings.role !== originalSettings.role ||
        settings.objective !== originalSettings.objective ||
        settings.phone_number !== originalSettings.phone_number ||
        settings.max_follow_ups !== originalSettings.max_follow_ups ||
        settings.whatsapp_account_id !== originalSettings.whatsapp_account_id ||
        settings.voice_notes_allowed !== originalSettings.voice_notes_allowed ||
        JSON.stringify(settings.languages) !== JSON.stringify(originalSettings.languages) ||
        JSON.stringify(settings.templates) !== JSON.stringify(originalSettings.templates) ||
        JSON.stringify(settings.do_rules) !== JSON.stringify(originalSettings.do_rules) ||
        JSON.stringify(settings.dont_rules) !== JSON.stringify(originalSettings.dont_rules);
      setHasConfigChanges(configChanged);
      onDirtyStateChange?.(configChanged);
    }
  }, [settings, originalSettings]);

  useEffect(() => {
    return () => { onDirtyStateChange?.(false); };
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    const [data, cId] = await Promise.all([
      getAgentSettings('whatsapp'),
      getWaCampaignId(),
    ]);
    if (data && !data.location && !locationInitialized) {
      const profile = await getUserProfile();
      if (profile?.country && COUNTRY_CAPITAL_MAP[profile.country]) {
        data.location = COUNTRY_CAPITAL_MAP[profile.country];
      }
      setLocationInitialized(true);
    }
    if (data?.location) {
      setLocationInput(data.location);
    }
    setSettings(data);
    setOriginalSettings(JSON.parse(JSON.stringify(data)));
    setCampaignId(cId);

    const result = await getWaTemplateSubmissions();
    setSubmissions(result.submissions);
    setApprovedKeys(result.approvedKeys);
    setLoading(false);
  };

  const loadWhatsAppAccounts = async () => {
    setWhatsappAccountsLoading(true);
    try {
      const accounts = await getConnectedWhatsAppAccounts();
      setWhatsappAccounts(accounts);
    } catch (error) {
      console.error('Error loading WhatsApp accounts:', error);
    }
    setWhatsappAccountsLoading(false);
  };

  const handleToggleVoiceNotes = async (enabled: boolean) => {
    if (!settings?.whatsapp_account_id) return;
    setSettings({ ...settings, voice_notes_allowed: enabled });
    await supabase
      .from('whatsapp_accounts')
      .update({ voice_notes_allowed: enabled })
      .eq('id', settings.whatsapp_account_id);
  };

  const handleToggleVideoMessages = async (enabled: boolean) => {
    if (!settings?.whatsapp_account_id) return;
    setSettings({ ...settings, video_messages_allowed: enabled });
    await supabase
      .from('whatsapp_accounts')
      .update({ video_messages_allowed: enabled })
      .eq('id', settings.whatsapp_account_id);
  };

  const handlePlayVoiceNote = () => {
    if (!settings?.voice_note_example) return;
    if (isPlayingVoiceNote && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlayingVoiceNote(false);
      return;
    }
    const audio = new Audio(settings.voice_note_example);
    audioRef.current = audio;
    audio.onended = () => setIsPlayingVoiceNote(false);
    audio.play();
    setIsPlayingVoiceNote(true);
  };

  const loadWaDrafts = async () => {
    setWaDraftsLoading(true);
    const drafts = await getWhatsAppDrafts();
    if (drafts.length > 0) {
      const hasFreshFromGeneration = drafts.some(d => d.source === 'sales_materials');

      if (hasFreshFromGeneration) {
        setWaDrafts(drafts);
      } else {
        setSettings(prev => {
          if (!prev) return prev;
          const currentTemplates = prev.templates || ({} as any);
          const newTemplates = { ...currentTemplates };
          for (const d of drafts) {
            const key = d.template_key as WhatsAppTemplateKey;
            newTemplates[key] = {
              text: d.submitted_text || '',
              button_text: d.submitted_button_text || '',
              button_url: d.submitted_button_url || '',
            };
          }
          const updated = { ...prev, templates: newTemplates, mode: 'template' as const };
          setOriginalSettings(JSON.parse(JSON.stringify(updated)));
          return updated;
        });
        setWaDrafts([]);
      }
    } else {
      setWaDrafts([]);
    }
    setWaDraftsLoading(false);
  };

  const applyWaDrafts = () => {
    if (!settings || waDrafts.length === 0) return;

    const currentTemplates = settings.templates || ({} as any);
    const newTemplates = { ...currentTemplates };

    for (const d of waDrafts) {
      const key = d.template_key as WhatsAppTemplateKey;
      newTemplates[key] = {
        text: d.submitted_text || '',
        button_text: d.submitted_button_text || '',
        button_url: d.submitted_button_url || '',
      };
    }

    setSettings({ ...settings, templates: newTemplates, mode: 'template' });
    setWaDrafts([]);
  };

  const handleSaveConfig = async () => {
    if (!settings) return;
    setSaving(true);
    const success = await saveAgentIdentityConfig('whatsapp', settings);
    if (success) {
      if (settings.templates) {
        const templatesMap: Record<string, { text: string; button_text?: string; button_url?: string }> = {};
        for (const [key, val] of Object.entries(settings.templates)) {
          if (val && typeof val === 'object' && 'text' in val) {
            const v = val as { text: string; button_text?: string; button_url?: string };
            templatesMap[key] = { text: v.text, button_text: v.button_text, button_url: v.button_url };
          }
        }
        await saveWhatsAppTemplatesAsDrafts(templatesMap);
      }

      setSaved(true);
      setLastSaved(new Date());
      setOriginalSettings(JSON.parse(JSON.stringify(settings)));
      setHasConfigChanges(false);
      triggerReplyPromptGeneration('whatsapp');
      setTimeout(() => setSaved(false), 2000);
    }
    setSaving(false);
  };

  const handleDiscardConfig = () => {
    if (originalSettings) {
      setSettings(JSON.parse(JSON.stringify(originalSettings)));
      setHasConfigChanges(false);
      onDirtyStateChange?.(false);
    }
  };

  useEffect(() => {
    if (saveRef) saveRef.current = handleSaveConfig;
    if (discardRef) discardRef.current = handleDiscardConfig;
  });

  const handleSubmitForApproval = async (
    key: WhatsAppTemplateKey,
    text: string,
    buttonText: string,
    buttonUrl: string
  ) => {
    setSubmittingKey(key);
    const result = await submitWaTemplateForApproval({
      templateKey: key,
      text,
      buttonText,
      buttonUrl,
      campaignId,
    });
    if (result.success && result.submission) {
      setSubmissions(prev => ({ ...prev, [key]: result.submission! }));
    }
    setSubmittingKey(null);
  };


  const handleUpdateTemplate = (key: WhatsAppTemplateKey, text: string) => {
    if (!settings) return;
    const currentTemplates = settings.templates || ({} as any);
    const currentEntry = currentTemplates[key] || { text: '', button_text: '', button_url: '' };
    setSettings({
      ...settings,
      templates: {
        ...currentTemplates,
        [key]: { ...currentEntry, text },
      },
    });
  };

  const handleUpdateButton = (key: WhatsAppTemplateKey, buttonText: string, buttonUrl: string) => {
    if (!settings) return;
    const currentTemplates = settings.templates || ({} as any);
    const currentEntry = currentTemplates[key] || { text: '' };
    setSettings({
      ...settings,
      templates: {
        ...currentTemplates,
        [key]: { ...currentEntry, button_text: buttonText, button_url: buttonUrl },
      },
    });
  };

  const handleGenerateMessage = async (section: string, tone: string, length: string, currentContent?: string): Promise<string> => {
    try {
      const profile = await getUserProfile();
      const response = await generateAgentScript({
        agent_type: 'whatsapp',
        section,
        company_name: profile?.company_name || undefined,
        tone,
        length,
        user_id: user?.email,
        client_id: user?.client_id,
        current_content: currentContent || undefined,
      });
      if (response?.generated_script) {
        return response.generated_script;
      }
      throw new Error('No script generated');
    } catch (error) {
      console.error('Error generating WhatsApp message:', error);
      throw error;
    }
  };

  const getConfiguredCount = (templates: typeof WA_TEMPLATE_DEFINITIONS) => {
    if (!settings?.templates) return 0;
    return templates.filter(t => {
      const hasApproved = approvedKeys.includes(t.key);
      return hasApproved;
    }).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Failed to load settings</p>
      </div>
    );
  }

  return (
    <div className={`max-w-full ${hasConfigChanges || saving || saved ? 'pb-28' : ''}`}>
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          <AgentAvatarUpload
            agentType="whatsapp"
            avatarUrl={agentAvatarUrl}
            onAvatarChange={setAgentAvatarUrl}
          />
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display mb-2">WhatsApp Agent</h2>
            <p className="text-gray-600">
              Configure your AI WhatsApp agent's messages and behavior
            </p>
          </div>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowTestModal(true)}
            className="px-4 py-2.5 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2 font-semibold"
          >
            <TestTube className="w-5 h-5" />
            Test Agent
          </button>
        )}
      </div>

      {!canEdit && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 mb-1">View Only</p>
            <p className="text-xs text-blue-800">
              Only admins can edit agent settings. Contact your admin to make changes.
            </p>
          </div>
        </div>
      )}

      <DraftBanner
        draftCount={waDrafts.length}
        agentType="WhatsApp"
        loading={waDraftsLoading}
        onApplyDrafts={applyWaDrafts}
        onDismissDrafts={() => setWaDrafts([])}
      />

      <div className={`space-y-6 ${!canEdit ? 'pointer-events-none opacity-75' : ''}`}>
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <UserCog className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 font-display">Agent Identity & Behavior</h3>
              <p className="text-sm text-gray-500">Configure your WhatsApp agent's identity and settings</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Left Column */}
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Agent Account</label>
                {whatsappAccountsLoading ? (
                  <div className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-400 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading accounts...
                  </div>
                ) : whatsappAccounts.length === 0 ? (
                  <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-400 bg-gray-50">
                    No accounts connected
                  </div>
                ) : (
                  <SimpleCustomSelect
                    value={settings.whatsapp_account_id || ''}
                    onChange={(value) => {
                      const account = whatsappAccounts.find(a => a.id === value);
                      if (account) {
                        setSettings({
                          ...settings,
                          whatsapp_account_id: account.id,
                          agent_name: account.agent_name || '',
                          phone_number: account.phone_number || '',
                          gender: account.agent_gender || '',
                          voice_notes_allowed: account.voice_notes_allowed ?? false,
                          voice_note_example: account.voice_note_example || '',
                          video_messages_allowed: account.video_messages_allowed ?? false,
                        });
                      }
                    }}
                    options={whatsappAccounts.map(a => ({
                      value: a.id,
                      label: `${a.agent_name} (${a.phone_number})`,
                    }))}
                    placeholder="Select account"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 min-h-[42px] flex items-center">
                  {settings.gender ? settings.gender.charAt(0).toUpperCase() + settings.gender.slice(1) : ''}
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nationality
                  {(settings.nationality?.length || 0) > 0 && (
                    <span className="ml-2 text-xs text-gray-400 font-normal">
                      {settings.nationality.length}/3
                    </span>
                  )}
                </label>
                <div
                  className="w-full min-h-[42px] px-3 py-1.5 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-[#1875FF]/50 focus-within:border-[#1875FF] text-sm transition-all hover:border-[#1875FF]/40 hover:shadow-lg hover:shadow-[#1875FF]/10 flex flex-wrap items-center gap-1.5 cursor-text"
                  onClick={() => {
                    const inp = document.getElementById('wa-nationality-typeahead');
                    if (inp) inp.focus();
                  }}
                >
                  {(settings.nationality || []).map(n => (
                    <span
                      key={n}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#1875FF]/10 text-[#1875FF] rounded-md text-xs font-semibold whitespace-nowrap flex-shrink-0"
                    >
                      {n}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSettings({
                            ...settings,
                            nationality: settings.nationality.filter(v => v !== n),
                          });
                        }}
                        className="hover:bg-[#1875FF]/20 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {(settings.nationality?.length || 0) < 3 ? (
                    <input
                      id="wa-nationality-typeahead"
                      type="text"
                      value={nationalityInput}
                      onChange={(e) => {
                        setNationalityInput(e.target.value);
                        setShowNationalitySuggestions(e.target.value.length > 0);
                      }}
                      onFocus={() => nationalityInput.length > 0 && setShowNationalitySuggestions(true)}
                      onBlur={() => setTimeout(() => setShowNationalitySuggestions(false), 200)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault();
                          const trimmed = nationalityInput.trim();
                          if (trimmed && !(settings.nationality || []).includes(trimmed)) {
                            setSettings({
                              ...settings,
                              nationality: [...(settings.nationality || []), trimmed],
                            });
                          }
                          setNationalityInput('');
                          setShowNationalitySuggestions(false);
                        }
                        if (e.key === 'Backspace' && !nationalityInput && (settings.nationality || []).length > 0) {
                          setSettings({
                            ...settings,
                            nationality: settings.nationality.slice(0, -1),
                          });
                        }
                      }}
                      placeholder={(settings.nationality || []).length === 0 ? 'Type nationality...' : ''}
                      className="flex-1 min-w-[80px] outline-none bg-transparent text-sm font-medium py-0.5"
                    />
                  ) : (
                    <span className="text-xs text-gray-400 py-0.5">Max 3 reached</span>
                  )}
                </div>
                {showNationalitySuggestions && (() => {
                  const filtered = COMMON_NATIONALITIES.filter(
                    n => n.toLowerCase().includes(nationalityInput.toLowerCase()) &&
                      !(settings.nationality || []).includes(n)
                  );
                  return filtered.length > 0 ? (
                    <div className="absolute z-10 mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 max-h-48 overflow-auto">
                      {filtered.map((nat, idx) => (
                        <button
                          key={nat}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (!(settings.nationality || []).includes(nat)) {
                              setSettings({
                                ...settings,
                                nationality: [...(settings.nationality || []), nat],
                              });
                            }
                            setNationalityInput('');
                            setShowNationalitySuggestions(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors font-medium ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                        >
                          {nat}
                        </button>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <input
                  type="text"
                  value={locationInput}
                  onChange={(e) => {
                    setLocationInput(e.target.value);
                    setShowLocationSuggestions(e.target.value.length > 0);
                    setSettings({ ...settings, location: e.target.value });
                  }}
                  onFocus={() => locationInput.length > 0 && setShowLocationSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setShowLocationSuggestions(false);
                    }
                  }}
                  placeholder="City, Country"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
                />
                {showLocationSuggestions && (() => {
                  const filtered = COMMON_LOCATIONS.filter(
                    loc => loc.toLowerCase().includes(locationInput.toLowerCase())
                  );
                  return filtered.length > 0 ? (
                    <div className="absolute z-10 mt-1 w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 max-h-48 overflow-auto">
                      {filtered.map((loc, idx) => (
                        <button
                          key={loc}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setLocationInput(loc);
                            setSettings({ ...settings, location: loc });
                            setShowLocationSuggestions(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors font-medium ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                        >
                          {loc}
                        </button>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Languages</label>
                <MultiSelectDropdown
                  value={settings.languages || ['english']}
                  onChange={(values) => setSettings({ ...settings, languages: values.includes('english') ? values : ['english', ...values] })}
                  options={[
                    { value: 'english', label: 'English' },
                    { value: 'french', label: 'French' },
                    { value: 'spanish', label: 'Spanish' },
                    { value: 'italian', label: 'Italian' },
                    { value: 'arabic', label: 'Arabic' },
                    { value: 'russian', label: 'Russian' },
                  ]}
                  placeholder="Select languages"
                  pinnedValues={['english']}
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-5">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  Messaging Style
                  <MessagingStyleTooltip agentType="whatsapp" />
                </label>
                <SimpleCustomSelect
                  value={settings.tone || 'friendly_casual'}
                  onChange={(value) => setSettings({ ...settings, tone: value })}
                  options={TONE_OPTIONS}
                  placeholder="Select style"
                />
              </div>

              <div className="flex items-start gap-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Voice Notes</label>
                  <div className="flex items-center gap-3 min-h-[42px]">
                    <button
                      type="button"
                      onClick={() => handleToggleVoiceNotes(!settings.voice_notes_allowed)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#1875FF]/50 focus:ring-offset-2 ${
                        settings.voice_notes_allowed
                          ? 'bg-gradient-to-r from-[#1875FF] to-[#05E0D1]'
                          : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform duration-200 ${
                          settings.voice_notes_allowed ? 'translate-x-[22px]' : 'translate-x-[2px]'
                        }`}
                      />
                    </button>
                    <span className="text-sm text-gray-600">
                      {settings.voice_notes_allowed ? 'Enabled' : 'Disabled'}
                    </span>
                    {settings.voice_notes_allowed && settings.voice_note_example && (
                      <button
                        type="button"
                        onClick={handlePlayVoiceNote}
                        className={`p-2 rounded-lg transition-all duration-200 ${
                          isPlayingVoiceNote
                            ? 'bg-[#1875FF]/15 text-[#1875FF] animate-pulse'
                            : 'bg-gray-100 text-gray-500 hover:bg-[#1875FF]/10 hover:text-[#1875FF]'
                        }`}
                        title={isPlayingVoiceNote ? 'Stop playing' : 'Play voice note example'}
                      >
                        <Volume2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Video Messages</label>
                  <div className="flex items-center gap-3 min-h-[42px]">
                    <button
                      type="button"
                      onClick={() => handleToggleVideoMessages(!settings.video_messages_allowed)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#1875FF]/50 focus:ring-offset-2 ${
                        settings.video_messages_allowed
                          ? 'bg-gradient-to-r from-[#1875FF] to-[#05E0D1]'
                          : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform duration-200 ${
                          settings.video_messages_allowed ? 'translate-x-[22px]' : 'translate-x-[2px]'
                        }`}
                      />
                    </button>
                    <span className="text-sm text-gray-600">
                      {settings.video_messages_allowed ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <input
                  type="text"
                  value={settings.role || ''}
                  onChange={(e) => setSettings({ ...settings, role: e.target.value })}
                  placeholder="Sales Consultant"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Objective</label>
                <input
                  type="text"
                  value={settings.objective || ''}
                  onChange={(e) => setSettings({ ...settings, objective: e.target.value })}
                  placeholder="Book a meeting"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Follow Ups</label>
                <input
                  type="number"
                  value={settings.max_follow_ups}
                  onChange={(e) => {
                    const val = Math.min(Math.max(parseInt(e.target.value) || 3, 2), 3);
                    setSettings({ ...settings, max_follow_ups: val });
                  }}
                  min="2"
                  max="3"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`mt-6 space-y-6 ${!canEdit ? 'opacity-75' : ''}`}>
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <ScrollText className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 font-display">Message Templates</h3>
              <p className="text-sm text-gray-500">Manage templates your agent sends via WhatsApp</p>
            </div>
          </div>

          <div className="space-y-5">
            <CategorySection
              title="Cold Outreach"
              icon={<MessageCircle className="w-5 h-5 text-[#1875FF]" />}
              templates={COLD_OUTREACH_TEMPLATES}
              settings={settings}
              onUpdateTemplate={handleUpdateTemplate}
              onUpdateButton={handleUpdateButton}
              onGenerateAI={handleGenerateMessage}
              configuredCount={getConfiguredCount(COLD_OUTREACH_TEMPLATES)}
              totalCount={COLD_OUTREACH_TEMPLATES.length}
              defaultOpen={false}
              submissions={submissions}
              approvedKeys={approvedKeys}
              onSubmitForApproval={handleSubmitForApproval}
              submittingKey={submittingKey}
              readOnly={!canEdit}
            />

            <CategorySection
              title="Inbound"
              icon={<ArrowDownCircle className="w-5 h-5 text-emerald-500" />}
              templates={INBOUND_TEMPLATES}
              settings={settings}
              onUpdateTemplate={handleUpdateTemplate}
              onUpdateButton={handleUpdateButton}
              onGenerateAI={handleGenerateMessage}
              configuredCount={getConfiguredCount(INBOUND_TEMPLATES)}
              totalCount={INBOUND_TEMPLATES.length}
              defaultOpen={false}
              submissions={submissions}
              approvedKeys={approvedKeys}
              onSubmitForApproval={handleSubmitForApproval}
              submittingKey={submittingKey}
              readOnly={!canEdit}
            />

            <CategorySection
              title="Other"
              icon={<Calendar className="w-5 h-5 text-[#05E0D1]" />}
              templates={OTHER_TEMPLATES}
              settings={settings}
              onUpdateTemplate={handleUpdateTemplate}
              onUpdateButton={handleUpdateButton}
              onGenerateAI={handleGenerateMessage}
              configuredCount={getConfiguredCount(OTHER_TEMPLATES)}
              totalCount={OTHER_TEMPLATES.length}
              defaultOpen={false}
              submissions={submissions}
              approvedKeys={approvedKeys}
              onSubmitForApproval={handleSubmitForApproval}
              submittingKey={submittingKey}
              readOnly={!canEdit}
            />
          </div>
        </div>

        <div className={!canEdit ? 'pointer-events-none' : ''}>
          <AgentRulesSection
            doRules={settings.do_rules}
            dontRules={settings.dont_rules}
            onDoRulesChange={(rules) => setSettings({ ...settings, do_rules: rules })}
            onDontRulesChange={(rules) => setSettings({ ...settings, dont_rules: rules })}
            subtitle="Define what your agent should and shouldn't do in messages"
          />
        </div>
      </div>

      {canEdit && (
        <FloatingSaveBar
          hasChanges={hasConfigChanges}
          saving={saving}
          saved={saved}
          lastSaved={lastSaved}
          onSave={handleSaveConfig}
          onDiscard={handleDiscardConfig}
        />
      )}

      <TestAgentModal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        agentType="whatsapp"
      />
    </div>
  );
}
